/**
 * SecurityDashboard — segunda pestaña del inicio: la postura de seguridad.
 *
 * POR QUÉ
 * ---------------------------------------------------------------------------
 * La pestaña «Resumen» cuenta lo que hay. Esta cuenta **cómo está**: agentes
 * caídos, equipos sin cifrar, parches pendientes, antivirus apagado, CVEs
 * abiertas y servicios sin un contacto al que llamar.
 *
 * Los números salen de columnas ya materializadas por el agente
 * (`workstations.bitlocker_state`, `updates_state`, `av_state`) y de conteos
 * agregados por tabla, así que la pantalla carga en una sola petición.
 *
 * Cada tarjeta enlaza a la lista YA FILTRADA de su problema: no es un adorno,
 * es la puerta de entrada al arreglo.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ShieldCheckIcon,
  ArrowPathIcon,
  LockClosedIcon,
  ArrowDownTrayIcon,
  ShieldExclamationIcon,
  BugAntIcon,
  CpuChipIcon,
  ComputerDesktopIcon,
  ServerStackIcon,
  VideoCameraIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';
import './SecurityDashboard.css';

function Anillo({ value }) {
  const size = 132;
  const stroke = 12;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const pct = Math.max(0, Math.min(100, Number(value) || 0));
  const offset = circumference - (pct / 100) * circumference;
  const color = pct >= 80 ? 'var(--success, #22c55e)' : pct >= 55 ? 'var(--warn, #f59e0b)' : 'var(--danger, #ef4444)';
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="sd-ring" role="img" aria-label={`Puntuación ${pct}`}>
      <circle cx={size / 2} cy={size / 2} r={radius} stroke="var(--border)" strokeWidth={stroke} fill="none" />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        stroke={color}
        strokeWidth={stroke}
        fill="none"
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
      <text x="50%" y="47%" textAnchor="middle" dominantBaseline="middle" className="sd-ring-value">{pct}</text>
      <text x="50%" y="63%" textAnchor="middle" className="sd-ring-label">/ 100</text>
    </svg>
  );
}

function TarjetaRiesgo({ icon: Icon, titulo, valor, detalle, tono, onClick }) {
  return (
    <button type="button" className={`sd-risk sd-risk--${tono}`} onClick={onClick}>
      <span className="sd-risk-icon"><Icon style={{ width: 20, height: 20 }} /></span>
      <span className="sd-risk-body">
        <span className="sd-risk-value">{valor}</span>
        <span className="sd-risk-title">{titulo}</span>
        {detalle && <span className="sd-risk-detail">{detalle}</span>}
      </span>
    </button>
  );
}

export default function SecurityDashboard({ companyId }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const cargar = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError('');
    try {
      setData(await api.getSecurityDashboard(companyId));
    } catch (err) {
      setError(err.message || 'No se pudo cargar el resumen de seguridad');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { cargar(); }, [cargar]);

  const metricas = useMemo(() => {
    if (!data) return null;
    const { protection = {}, agents = {}, vulnerabilities = {}, endpoints = {}, exposure = {} } = data;
    return {
      protection,
      agents,
      vulnerabilities,
      endpoints,
      exposure,
    };
  }, [data]);

  if (!companyId) {
    return (
      <div className="sd-empty">
        <ShieldCheckIcon style={{ width: 34, height: 34, opacity: 0.6 }} />
        <p>Selecciona una empresa para ver su postura de seguridad.</p>
      </div>
    );
  }

  return (
    <div className="sd-page">
      <div className="sd-head">
        <div>
          <h2 className="sd-title">Postura de seguridad</h2>
          <p className="sd-subtitle">
            Equipos, cifrado, parches, antivirus y vulnerabilidades de servidores, estaciones y máquinas virtuales.
          </p>
        </div>
        <button type="button" className="db-refresh-btn" onClick={cargar} disabled={loading}>
          <ArrowPathIcon style={{ width: 15, height: 15 }} /> {loading ? 'Actualizando…' : 'Actualizar'}
        </button>
      </div>

      {error && <p className="sd-error">{error}</p>}
      {loading && !data && <p className="list-hint">Calculando la postura de seguridad…</p>}

      {metricas && (
        <>
          <div className="sd-hero">
            <Anillo value={data.score} />
            <div className="sd-hero-text">
              <h3>
                {data.score >= 80 ? 'Bien encaminado'
                  : data.score >= 55 ? 'Hay que cerrar huecos'
                    : 'Riesgo alto'}
              </h3>
              <p>
                {metricas.protection.bitlocker_missing > 0
                  ? `Hay ${metricas.protection.bitlocker_missing} equipo(s) sin cifrado. `
                  : 'El cifrado de disco está al día. '}
                {metricas.protection.updates_pending > 0
                  ? `Quedan ${metricas.protection.updates_pending} equipo(s) con parches pendientes.`
                  : 'Sin parches pendientes. '}
              </p>
              <div className="sd-hero-chips">
                <span className="list-badge list-badge--accent">
                  <ComputerDesktopIcon style={{ width: 12, height: 12 }} /> {metricas.endpoints.workstations} estaciones
                </span>
                <span className="list-badge list-badge--accent">
                  <ServerStackIcon style={{ width: 12, height: 12 }} /> {metricas.endpoints.servers} servidores
                </span>
                <span className="list-badge list-badge--accent">
                  <CpuChipIcon style={{ width: 12, height: 12 }} /> {metricas.endpoints.vms} VMs
                </span>
              </div>
            </div>
          </div>

          <div className="sd-grid">
            <TarjetaRiesgo
              icon={ShieldExclamationIcon}
              titulo="Agentes caídos"
              valor={metricas.agents.offline}
              detalle={`${metricas.agents.online} en línea · ${metricas.agents.unknown} sin agente`}
              tono={metricas.agents.offline > 0 ? 'warn' : 'ok'}
              onClick={() => navigate('/infrastructure/workstations/list?agent=offline')}
            />
            <TarjetaRiesgo
              icon={LockClosedIcon}
              titulo="Sin cifrado (BitLocker)"
              valor={metricas.protection.bitlocker_missing}
              detalle={`${metricas.protection.bitlocker_unknown} sin dato`}
              tono={metricas.protection.bitlocker_missing > 0 ? 'bad' : 'ok'}
              onClick={() => navigate('/infrastructure/workstations/list?bitlocker=unprotected')}
            />
            <TarjetaRiesgo
              icon={ArrowDownTrayIcon}
              titulo="Parches pendientes"
              valor={metricas.protection.updates_pending}
              detalle={`${metricas.protection.updates_critical} críticos · ${metricas.protection.updates_reboot} con reinicio`}
              tono={metricas.protection.updates_pending > 0 ? 'warn' : 'ok'}
              onClick={() => navigate('/infrastructure/workstations/list?updates=pending')}
            />
            <TarjetaRiesgo
              icon={ShieldCheckIcon}
              titulo="Antivirus apagado"
              valor={metricas.protection.av_off}
              detalle={`${metricas.protection.av_unknown} sin dato`}
              tono={metricas.protection.av_off > 0 ? 'bad' : 'ok'}
              onClick={() => navigate('/infrastructure/workstations/list?av=off')}
            />
            <TarjetaRiesgo
              icon={BugAntIcon}
              titulo="CVEs abiertas"
              valor={metricas.vulnerabilities.open}
              detalle={`${metricas.vulnerabilities.critical} críticas · ${metricas.vulnerabilities.high} altas`}
              tono={metricas.vulnerabilities.critical > 0 ? 'bad' : metricas.vulnerabilities.high > 0 ? 'warn' : 'ok'}
              onClick={() => navigate('/security/vulnerabilities')}
            />
            <TarjetaRiesgo
              icon={VideoCameraIcon}
              titulo="Servicios sin responsable"
              valor={metricas.exposure.services_without_contact}
              detalle={`de ${metricas.exposure.services_total} servicios del catálogo`}
              tono={metricas.exposure.services_without_contact > 0 ? 'warn' : 'ok'}
              onClick={() => navigate('/services')}
            />
          </div>

          <div className="sd-columns">
            <section className="sd-card">
              <h4>Inventario bajo vigilancia</h4>
              {[
                ['Estaciones de trabajo', metricas.endpoints.workstations, `${metricas.endpoints.laptops} portátiles`],
                ['Servidores', metricas.endpoints.servers, 'elementos de rack'],
                ['Máquinas virtuales', metricas.endpoints.vms, `${metricas.endpoints.vms_running} encendidas`],
                ['Cobertura del agente', `${metricas.agents.coverage}%`, `${metricas.agents.online + metricas.agents.offline} equipos con agente`],
              ].map(([label, value, detail]) => (
                <div key={label} className="sd-row">
                  <span>{label}</span>
                  <span className="sd-row-value">{value}</span>
                  <span className="list-hint">{detail}</span>
                </div>
              ))}
            </section>

            <section className="sd-card">
              <h4>Qué hacer ahora</h4>
              <ol className="sd-actions">
                {metricas.protection.bitlocker_missing > 0 && (
                  <li>Activar BitLocker en {metricas.protection.bitlocker_missing} equipo(s): son equipos portátiles o de escritorio con disco sin cifrar.</li>
                )}
                {metricas.protection.updates_pending > 0 && (
                  <li>Aprobar y desplegar parches en {metricas.protection.updates_pending} equipo(s) ({metricas.protection.updates_critical} críticos).</li>
                )}
                {metricas.agents.offline > 0 && (
                  <li>Revisar {metricas.agents.offline} agente(s) sin conexión: sin agente no hay inventario ni parches.</li>
                )}
                {metricas.vulnerabilities.critical > 0 && (
                  <li>Atender {metricas.vulnerabilities.critical} CVE(s) críticas abiertas.</li>
                )}
                {metricas.exposure.services_without_contact > 0 && (
                  <li>Nombrar responsable de {metricas.exposure.services_without_contact} servicio(s) en Contactos.</li>
                )}
                {data.score >= 80 && (
                  <li>Nada urgente. Mantener la revisión semanal de agentes y parches.</li>
                )}
              </ol>
            </section>
          </div>

          <p className="list-hint sd-foot">
            Calculado {data.generated_at ? new Date(data.generated_at).toLocaleString() : ''} ·
            los datos vienen del agente CMDB, no de un sondeo en vivo.
          </p>
        </>
      )}
    </div>
  );
}
